import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, FormHelperText, TextField } from '@mui/material';

import { Themes } from '../../../constants';
import {
  FORTA_BLACK,
  FORTA_WHITE,
  FORTA_ERROR,
  FORTA_BLUE,
  FORTA_SUCCESS,
  FORTA_GRAY2,
} from '../../../utils/colors';
import { getThemeColor } from '../../../utils/common';
import { DEFAULT_EXCEL_FIELD_HEIGHT, DEFAULT_EXCEL_FIELD_WIDTH } from './constants';
import { cleanNumericInputValue } from '../../../helpers';

import type { FormHelperTextProps, TextFieldProps as MTextFieldProps } from '@mui/material';
import type { ChangeEvent, ReactNode } from 'react';

type TextFieldProps = MTextFieldProps & {
  colorTheme?: Themes;
  coloredBackground?: boolean;
  inputHeight?: number;
  error?: boolean;
  infoText?: ReactNode;
  inputType?: 'number' | 'text';
  minNumericValue?: number;
  maxNumericValue?: number;
  onNumericValueError?: (value: boolean) => void;
};

const StyledInputField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'inputHeight' &&
    prop !== 'error',
})<TextFieldProps>(({ colorTheme, coloredBackground, inputHeight, error, sx = {} }) => ({
  backgroundColor: coloredBackground
    ? getThemeColor(colorTheme ?? Themes.Primary).light3
    : FORTA_WHITE,
  borderRadius: '4px',
  overflow: 'hidden',
  minWidth: DEFAULT_EXCEL_FIELD_WIDTH,
  border: error ? `1px solid ${FORTA_ERROR}` : `1px solid ${FORTA_GRAY2}`,

  '& .MuiInputBase-root': {
    color: FORTA_BLACK,
    '&.Mui-disabled': {
      color: FORTA_BLACK,
      backgroundColor: coloredBackground
        ? getThemeColor(colorTheme ?? Themes.Primary).light3
        : FORTA_WHITE,
    },

    '& .MuiOutlinedInput-input': {
      height: inputHeight,
      boxSizing: 'border-box',
      padding: '0.5em',

      '&.Mui-disabled': {
        WebkitTextFillColor: FORTA_BLACK,
        cursor: 'not-allowed',
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '&.MuiFilledInput-root': {
      background: 'transparent',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },
    ...sx,
  },
}));

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'fullHeight',
})<FormHelperTextProps>(({ error }: any) => ({
  color:
    error === null || error === undefined
      ? `${FORTA_BLUE.primary}`
      : error === false
        ? `${FORTA_SUCCESS}`
        : `${FORTA_ERROR}`,
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '21px',
}));

const FHExcelInput = (props: TextFieldProps) => {
  const {
    colorTheme = Themes.Primary,
    coloredBackground = false,
    inputHeight = DEFAULT_EXCEL_FIELD_HEIGHT,
    error = false,
    minNumericValue = 0,
    maxNumericValue = 10000000, // 10M
  } = props;
  const {
    helperText,
    infoText,
    onChange,
    inputType = 'text',
    onNumericValueError,
    ...inputProps
  } = props;

  const [numericValueError, setNumericValueError] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <StyledInputField
        colorTheme={colorTheme}
        coloredBackground={coloredBackground}
        inputHeight={inputHeight}
        error={error || numericValueError}
        {...inputProps}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (!onChange) return;
          if (inputType === 'text') {
            onChange(e);
          } else {
            onChange(e);
            const value = parseFloat(cleanNumericInputValue(e.target.value));
            if (value > maxNumericValue || value < minNumericValue) {
              setNumericValueError(true);
              if (onNumericValueError) {
                onNumericValueError(true);
              }
            } else {
              setNumericValueError(false);
              if (onNumericValueError) {
                onNumericValueError(false);
              }
            }
          }
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: infoText ? 'space-between' : 'end',
          alignItems: 'center',
        }}
      >
        {infoText}
        <StyledFormHelperText error={error}>
          <span>{helperText}</span>
        </StyledFormHelperText>
      </Box>
    </Box>
  );
};

export default FHExcelInput;
